import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  FaFilePdf, FaFileWord, FaFileImage, FaFileAlt, FaUpload, FaPen, FaArchive, FaUndo, FaExternalLinkAlt, FaTimes,
} from "react-icons/fa";
import { useAuth } from "../../../../Context/AuthContext";
import { useServiceConfig } from "../../../../hooks/useServiceConfig";
import { useServiceApi } from "../../../../hooks/useServiceApi";
import { usePermission } from "../../../../permissions/usePermission";
import { useNotification } from "../../../../Context/NotificationContext";
import useAgmOrganisation from "./useAgmOrganisation";
import "../../../../styles/feedAgm.css";

// AGM & Board meeting documents (/{service}/agm-board/agm and /board): one page per meeting type, its sections as
// tabs. Each section holds any number of documents, each with its own name and date; Year / Month filters and
// sorting work on that date. Admins of the organisation upload (several files at once), rename, re-date and
// archive; members only read - archived documents included, behind "Show archived". Server: AgmDocumentController.
//
// The old one-file-per-section links (…/agm/bylaws_view, …/board/duties_upload) still work: the last part of the
// address picks the section, and *_upload opens the upload form.

export const AGM_MODULES = {
  AGM: {
    title: "Annual General Meeting",
    other: { key: "BOD", label: "Board of Directors Meeting", path: "board" },
    sections: [
      { key: "BYLAWS_MOA_AOA", label: "Bye-laws / MOA / AOA", slug: "bylaws",
        categories: [{ key: "BYLAWS", label: "Bye-laws" }, { key: "MOA", label: "Memorandum of Association (MOA)" }, { key: "AOA", label: "Articles of Association (AOA)" }] },
      { key: "MEMBER_RIGHTS", label: "Member Rights", slug: "memberrights" },
      { key: "AGM_NOTICES", label: "Notices", slug: "agmnotices" },
      { key: "AGM_RESOLUTIONS", label: "Resolutions", slug: "agmresolutions" },
    ],
  },
  BOD: {
    title: "Board of Directors Meeting",
    other: { key: "AGM", label: "Annual General Meeting", path: "agm" },
    sections: [
      { key: "DIRECTOR_DUTIES", label: "Duties of Directors", slug: "duties" },
      { key: "DIRECTOR_POWERS", label: "Powers of Directors", slug: "power" },
      { key: "DIRECTORS_LIST", label: "List of Directors", slug: "list" },
      { key: "BOD_NOTICES", label: "Notices", slug: "bodnotices" },
      { key: "BOD_RESOLUTIONS", label: "Resolutions", slug: "bodresolutions" },
    ],
  },
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
const ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png", "doc", "docx"];
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 20;

const today = () => new Date().toISOString().slice(0, 10);
const ext = (name = "") => (name.split(".").pop() || "").toLowerCase();
const baseName = (name = "") => name.replace(/\.[^.]+$/, "");
export const sizeText = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
export const dateText = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1].slice(0, 3)} ${y}`;
};
const errorText = (err, fallback) =>
  err?.response?.data?.error?.message || err?.response?.data?.message || fallback;

// Stored files are served only with the sign-in token (same as DocumentViewier.jsx), so a plain link can't open
// them: fetch with the token, then show it in a new tab (PDF, images) or save it (Word - browsers can't show it).
// The tab is opened before the fetch so the browser doesn't treat it as an unrequested pop-up. Also used by the
// System Administrator's AGM Documents page.
export async function openStoredFile(doc, accessToken) {
  const isWord = ["doc", "docx"].includes(ext(doc.originalFileName || doc.fileUrl));
  const win = isWord ? null : window.open("", "_blank");
  try {
    const res = await fetch(doc.fileUrl, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!res.ok) throw new Error(String(res.status));
    const blobUrl = URL.createObjectURL(await res.blob());
    if (win) {
      win.location.href = blobUrl;
    } else {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = doc.originalFileName || `${doc.name}.${ext(doc.fileUrl)}`;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    return true;
  } catch {
    if (win) win.close();
    return false;
  }
}

export function FileIcon({ doc }) {
  const e = ext(doc.originalFileName || doc.fileUrl);
  if (e === "pdf") return <FaFilePdf className="agm-ficon is-pdf" aria-hidden="true" />;
  if (e === "doc" || e === "docx") return <FaFileWord className="agm-ficon is-word" aria-hidden="true" />;
  if (["jpg", "jpeg", "png"].includes(e)) return <FaFileImage className="agm-ficon is-image" aria-hidden="true" />;
  return <FaFileAlt className="agm-ficon" aria-hidden="true" />;
}

export function Modal({ title, onClose, children, footer }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    ref.current?.querySelector("input, select, button")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="agm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="agm-dialog" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="agm-dialog-head">
          <h2>{title}</h2>
          <button type="button" className="agm-icon-btn" aria-label="Close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="agm-dialog-body">{children}</div>
        {footer && <div className="agm-dialog-foot">{footer}</div>}
      </div>
    </div>
  );
}

export default function AgmDocumentsPage({ module }) {
  const def = AGM_MODULES[module];
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { route } = useServiceConfig();
  const api = useServiceApi();
  const { accessToken } = useAuth();
  const { canCreate, canEdit } = usePermission("agmBoard");
  const { showSuccess, showError } = useNotification();

  // The organisation, and whether this account manages its documents (admins upload / edit / archive, members
  // only view). usePermission alone isn't enough: it lets a System Administrator through everywhere, and they
  // have no organisation here - see useAgmOrganisation.
  const { loading: orgLoading, organizationId, isAdmin: isOrgAdmin } = useAgmOrganisation();
  const mayUpload = isOrgAdmin && canCreate;
  const mayEdit = isOrgAdmin && canEdit;

  // Section: ?section=, else an old-style address (…/bylaws_view, …/bylaws_upload), else the first one
  const lastPart = location.pathname.split("/").filter(Boolean).pop() || "";
  const oldSlug = lastPart.replace(/_(view|upload)$/, "");
  const fromOld = def.sections.find((s) => s.slug === oldSlug);
  const sectionKey = def.sections.some((s) => s.key === searchParams.get("section"))
    ? searchParams.get("section")
    : fromOld?.key || def.sections[0].key;
  const section = def.sections.find((s) => s.key === sectionKey);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [sort, setSort] = useState("newest");
  const [showArchived, setShowArchived] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [archiving, setArchiving] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setLoadError("");
    try {
      const res = await api.getAgmDocuments(organizationId);
      setDocs(res.data?.data || []);
    } catch (err) {
      setLoadError(errorText(err, "Could not load the documents."));
    } finally {
      setLoading(false);
    }
  }, [api, organizationId]);

  useEffect(() => { load(); }, [load]);

  // Old *_upload links open the form once; the address then becomes the page's own. Runs on every address change:
  // moving between AGM and Board reuses this component instead of mounting a new one.
  useEffect(() => {
    if (fromOld || searchParams.get("upload") === "1") {
      if (mayUpload && (/_upload$/.test(lastPart) || searchParams.get("upload") === "1")) setUploadOpen(true);
      navigate(`${route}/agm-board/${module === "AGM" ? "agm" : "board"}?section=${sectionKey}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mayUpload, location.pathname, location.search, module]);

  const pickSection = (key) => {
    setSearchParams({ section: key }, { replace: true });
    setYear("");
    setMonth("");
    setQuery("");
  };

  const sectionDocs = useMemo(() => docs.filter((d) => d.section === sectionKey), [docs, sectionKey]);
  const archivedCount = sectionDocs.filter((d) => d.archived).length;
  const years = useMemo(
    () => [...new Set(sectionDocs.map((d) => (d.documentDate || "").slice(0, 4)).filter(Boolean))].sort().reverse(),
    [sectionDocs]
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = sectionDocs.filter((d) => {
      if (!showArchived && d.archived) return false;
      if (year && !(d.documentDate || "").startsWith(year)) return false;
      if (month && (d.documentDate || "").slice(5, 7) !== month) return false;
      if (q && !`${d.name} ${d.originalFileName || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const by = {
      newest: (a, b) => (b.documentDate || "").localeCompare(a.documentDate || "") || (b.createDate || 0) - (a.createDate || 0),
      oldest: (a, b) => (a.documentDate || "").localeCompare(b.documentDate || ""),
      name: (a, b) => a.name.localeCompare(b.name),
    }[sort];
    return [...list].sort(by);
  }, [sectionDocs, showArchived, year, month, query, sort]);

  const groups = section.categories
    ? section.categories.map((c) => ({ ...c, docs: shown.filter((d) => d.category === c.key) }))
    : [{ key: "all", label: null, docs: shown }];

  const filtersOn = year || month || query;
  const counts = useMemo(() => {
    const out = {};
    def.sections.forEach((s) => { out[s.key] = docs.filter((d) => d.section === s.key && !d.archived).length; });
    return out;
  }, [docs, def]);

  const openDoc = async (doc) => {
    if (!(await openStoredFile(doc, accessToken))) showError("Could not open the document. Please try again.");
  };

  const setArchived = async (doc, archived) => {
    try {
      await api.updateAgmDocument(organizationId, doc.id, { archived });
      showSuccess(archived ? `"${doc.name}" archived.` : `"${doc.name}" is back in the list.`);
      setArchiving(null);
      load();
    } catch (err) {
      showError(errorText(err, "Could not change the document."));
    }
  };

  if (orgLoading) {
    return <div className="agm-page"><div className="agm-empty">Loading…</div></div>;
  }
  if (!organizationId) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-warning">
          AGM &amp; Board documents belong to an individual organisation. This account is not associated with one.
        </div>
      </div>
    );
  }

  return (
    <div className="agm-page">
      <div className="agm-head">
        <div>
          <Link to={`${route}/agm-board`} className="agm-back">← AGM &amp; Board</Link>
          <h1 className="agm-title">{def.title}</h1>
          <p className="agm-sub">
            {mayUpload ? "Upload, name and archive the meeting documents. Members can view them." : "Meeting documents of your organisation."}
          </p>
        </div>
        <div className="agm-head-actions">
          <Link to={`${route}/agm-board/${def.other.path}`} className="agm-btn">{def.other.label} →</Link>
          {mayUpload && (
            <button type="button" className="agm-btn is-primary" onClick={() => setUploadOpen(true)}>
              <FaUpload aria-hidden="true" /> Upload
            </button>
          )}
        </div>
      </div>

      <div className="agm-tabs" role="tablist" aria-label={`${def.title} sections`}>
        {def.sections.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={s.key === sectionKey}
            className={`agm-tab${s.key === sectionKey ? " is-on" : ""}`}
            onClick={() => pickSection(s.key)}
          >
            {s.label}
            <span className="agm-count">{counts[s.key] || 0}</span>
          </button>
        ))}
      </div>

      <div className="agm-tools">
        <label className="agm-field is-grow">
          <span>Search</span>
          <input type="search" className="form-control" placeholder="Document name" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label className="agm-field">
          <span>Year</span>
          <select className="form-select" value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="agm-field">
          <span>Month</span>
          <select className="form-select" value={month} onChange={(e) => setMonth(e.target.value)}>
            <option value="">All months</option>
            {MONTHS.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
          </select>
        </label>
        <label className="agm-field">
          <span>Sort</span>
          <select className="form-select" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name A–Z</option>
          </select>
        </label>
        <label className="agm-switch">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          <span>Show archived{archivedCount ? ` (${archivedCount})` : ""}</span>
        </label>
      </div>

      {loading && <div className="agm-empty">Loading documents…</div>}
      {!loading && loadError && (
        <div className="agm-empty">{loadError} <button type="button" className="btn btn-link p-0" onClick={load}>Try again</button></div>
      )}
      {!loading && !loadError && groups.map((g) => (
        <section key={g.key} className="agm-group" aria-label={g.label || section.label}>
          {g.label && (
            <h2 className="agm-group-title">{g.label}<span className="agm-count">{g.docs.length}</span></h2>
          )}
          {g.docs.length === 0 ? (
            <p className="agm-empty">
              {filtersOn ? "No documents match these filters." : mayUpload ? "No documents yet. Use Upload to add one." : "No documents yet."}
            </p>
          ) : (
            <ul className="agm-list">
              {g.docs.map((d) => (
                <li key={d.id} className={`agm-doc${d.archived ? " is-archived" : ""}`}>
                  <FileIcon doc={d} />
                  <div className="agm-doc-main">
                    <button type="button" className="agm-doc-name" onClick={() => openDoc(d)}>{d.name}</button>
                    <div className="agm-doc-meta">
                      <span>{dateText(d.documentDate)}</span>
                      {d.originalFileName && <span>{ext(d.originalFileName).toUpperCase()}</span>}
                      {d.fileSize ? <span>{sizeText(d.fileSize)}</span> : null}
                      {d.archived && <span className="agm-badge">Archived</span>}
                    </div>
                  </div>
                  <div className="agm-doc-actions">
                    <button type="button" className="agm-btn is-small" onClick={() => openDoc(d)} aria-label={`Open ${d.name}`}>
                      <FaExternalLinkAlt aria-hidden="true" /> <span>Open</span>
                    </button>
                    {mayEdit && (
                      <>
                        <button type="button" className="agm-btn is-small" onClick={() => setEditing(d)} aria-label={`Edit ${d.name}`}>
                          <FaPen aria-hidden="true" /> <span>Edit</span>
                        </button>
                        {d.archived ? (
                          <button type="button" className="agm-btn is-small" onClick={() => setArchived(d, false)} aria-label={`Bring back ${d.name}`}>
                            <FaUndo aria-hidden="true" /> <span>Bring back</span>
                          </button>
                        ) : (
                          <button type="button" className="agm-btn is-small" onClick={() => setArchiving(d)} aria-label={`Archive ${d.name}`}>
                            <FaArchive aria-hidden="true" /> <span>Archive</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      {uploadOpen && (
        <UploadDialog
          section={section}
          moduleTitle={def.title}
          onClose={() => setUploadOpen(false)}
          onDone={() => { setUploadOpen(false); load(); }}
          upload={(fd) => api.uploadAgmDocuments(organizationId, fd)}
        />
      )}
      {editing && (
        <EditDialog
          doc={editing}
          section={section}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
          save={(data) => api.updateAgmDocument(organizationId, editing.id, data)}
        />
      )}
      {archiving && (
        <Modal
          title="Archive this document?"
          onClose={() => setArchiving(null)}
          footer={(
            <>
              <button type="button" className="agm-btn" onClick={() => setArchiving(null)}>Cancel</button>
              <button type="button" className="agm-btn is-primary" onClick={() => setArchived(archiving, true)}>Archive</button>
            </>
          )}
        >
          <p className="mb-0">
            <strong>{archiving.name}</strong> moves out of the list. It is not deleted: anyone can still see it with
            “Show archived”, and you can bring it back at any time.
          </p>
        </Modal>
      )}
    </div>
  );
}

function UploadDialog({ section, moduleTitle, onClose, onDone, upload }) {
  const { showSuccess, showError } = useNotification();
  const [category, setCategory] = useState("");
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const problems = [];
    const ok = [];
    incoming.forEach((f) => {
      if (!ALLOWED_EXT.includes(ext(f.name))) problems.push(`${f.name}: only PDF, JPG, PNG and Word files.`);
      else if (f.size > MAX_BYTES) problems.push(`${f.name}: larger than 10 MB.`);
      else ok.push({ file: f, name: baseName(f.name), key: `${f.name}-${f.size}-${Math.random()}` });
    });
    setRows((r) => [...r, ...ok].slice(0, MAX_FILES));
    setErrors((e) => ({ ...e, files: problems.join(" ") || undefined }));
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    const found = {};
    if (section.categories && !category) found.category = "Choose Bye-laws, MOA or AOA.";
    if (!date) found.date = "Choose the document date.";
    if (rows.length === 0) found.files = "Choose at least one file.";
    rows.forEach((r, i) => { if (!r.name.trim()) found[`name${i}`] = "Give this document a name."; });
    setErrors(found);
    if (Object.keys(found).length) return;

    const fd = new FormData();
    fd.append("section", section.key);
    if (section.categories) fd.append("category", category);
    fd.append("documentDate", date);
    rows.forEach((r) => { fd.append("files", r.file); fd.append("names", r.name.trim()); });
    setSaving(true);
    try {
      await upload(fd);
      showSuccess(rows.length === 1 ? "Document uploaded." : `${rows.length} documents uploaded.`);
      onDone();
    } catch (err) {
      showError(errorText(err, "Could not upload. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Upload to ${section.label}`}
      onClose={onClose}
      footer={(
        <>
          <span className="agm-muted me-auto">{moduleTitle}</span>
          <button type="button" className="agm-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="agm-btn is-primary" onClick={submit} disabled={saving}>
            {saving ? "Uploading…" : rows.length > 1 ? `Upload ${rows.length} documents` : "Upload"}
          </button>
        </>
      )}
    >
      {section.categories && (
        <label className="agm-field is-block">
          <span>Document type</span>
          <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)} aria-invalid={!!errors.category}>
            <option value="">Choose…</option>
            {section.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {errors.category && <em className="agm-error">{errors.category}</em>}
        </label>
      )}
      <label className="agm-field is-block">
        <span>Document date</span>
        <input type="date" className="form-control" value={date} max={today()} onChange={(e) => setDate(e.target.value)} aria-invalid={!!errors.date} />
        <small className="agm-muted">The date of the meeting, notice or resolution. The Year and Month filters use it.</small>
        {errors.date && <em className="agm-error">{errors.date}</em>}
      </label>

      <div className="agm-field is-block">
        <span>Files</span>
        <label className="agm-drop">
          <input ref={inputRef} type="file" multiple accept={ACCEPT} onChange={(e) => addFiles(e.target.files)} />
          <FaUpload aria-hidden="true" />
          <strong>Choose files</strong>
          <small>PDF, JPG, PNG or Word · up to 10 MB each · up to {MAX_FILES} at once</small>
        </label>
        {errors.files && <em className="agm-error">{errors.files}</em>}
      </div>

      {rows.length > 0 && (
        <ul className="agm-upload-rows">
          {rows.map((r, i) => (
            <li key={r.key}>
              <label className="agm-field is-grow">
                <span>Name for {r.file.name} <small className="agm-muted">({sizeText(r.file.size)})</small></span>
                <input
                  type="text"
                  className="form-control"
                  maxLength={200}
                  value={r.name}
                  aria-invalid={!!errors[`name${i}`]}
                  onChange={(e) => setRows((all) => all.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                />
                {errors[`name${i}`] && <em className="agm-error">{errors[`name${i}`]}</em>}
              </label>
              <button type="button" className="agm-icon-btn" aria-label={`Remove ${r.file.name}`} onClick={() => setRows((all) => all.filter((_, j) => j !== i))}>
                <FaTimes />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function EditDialog({ doc, section, onClose, onDone, save }) {
  const { showSuccess, showError } = useNotification();
  const [name, setName] = useState(doc.name);
  const [category, setCategory] = useState(doc.category || "");
  const [date, setDate] = useState(doc.documentDate || today());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { setError("Give the document a name."); return; }
    const data = {};
    if (name.trim() !== doc.name) data.name = name.trim();
    if (section.categories && category !== doc.category) data.category = category;
    if (date !== doc.documentDate) data.documentDate = date;
    if (!Object.keys(data).length) { onClose(); return; }
    setSaving(true);
    try {
      await save(data);
      showSuccess("Document updated.");
      onDone();
    } catch (err) {
      showError(errorText(err, "Could not save. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit document"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="agm-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="agm-btn is-primary" onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </>
      )}
    >
      <label className="agm-field is-block">
        <span>Name</span>
        <input type="text" className="form-control" maxLength={200} value={name} onChange={(e) => { setName(e.target.value); setError(""); }} aria-invalid={!!error} />
        {error && <em className="agm-error">{error}</em>}
      </label>
      {section.categories && (
        <label className="agm-field is-block">
          <span>Document type</span>
          <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
            {section.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>
      )}
      <label className="agm-field is-block">
        <span>Document date</span>
        <input type="date" className="form-control" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
      </label>
      <p className="agm-muted mb-0">File: {doc.originalFileName || "—"}. To replace the file, upload the new one and archive this one.</p>
    </Modal>
  );
}
